import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import MetaTags from '../components/SEO/MetaTags';
import { blogPosts } from '../data/blog';
import {
  CalendarIcon,
  ClockIcon,
  TagIcon,
  UserIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

const Blog = () => {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div>
      <MetaTags
        title="Blog - Expert Financial Insights | NAQD Consulting"
        description="Stay updated with the latest tax laws, business compliance tips, and financial planning strategies from Kerala's leading chartered accountants."
        keywords="chartered accountant blog, tax planning articles, business compliance tips, GST updates, NRI taxation guide"
      />

      {/* Hero Section */}
      <section
        className="section-padding relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/assets/images/backgrounds/tax-hero.png')" }}
      >
        <div className="absolute inset-0 bg-naqd-primary/80"></div>
        <div className="container-custom text-center relative z-10">
          <div className="animate-on-scroll">
            <h1 className="heading-primary mb-6 text-white">
              Expert <span className="text-naqd-green">Financial Insights</span>
            </h1>
            <p className="text-xl text-white/90 dark:text-gray-300 max-w-4xl mx-auto">
              Stay informed with the latest tax laws, compliance strategies, and
              financial planning tips from Kerala's leading chartered accountants.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <article
                key={post.id}
                className="card group animate-on-scroll"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <Link to={`/blog/${post.slug}`}>
                  <div className="aspect-w-16 aspect-h-9 bg-gray-700 rounded-lg mb-4 overflow-hidden">
                    <div className="w-full h-48 bg-gradient-to-br from-naqd-green/20 to-naqd-green/10 flex items-center justify-center">
                      <span className="text-4xl font-bold text-naqd-green">{post.title.charAt(0)}</span>
                    </div>
                  </div>
                </Link>

                <div className="space-y-3">
                  <div className="flex items-center space-x-4 text-sm text-gray-400">
                    <span className="flex items-center">
                      <CalendarIcon className="w-4 h-4 mr-1" />
                      {post.date}
                    </span>
                    <span className="flex items-center">
                      <ClockIcon className="w-4 h-4 mr-1" />
                      {post.readTime}
                    </span>
                  </div>

                  <div>
                    <span className="inline-block bg-naqd-green/10 text-naqd-green text-xs px-2 py-1 rounded-full">
                      {post.category}
                    </span>
                  </div>

                  <Link to={`/blog/${post.slug}`}>
                    <h3 className="text-xl font-semibold group-hover:text-naqd-green transition-colors"
                    >
                      {post.title}
                    </h3>
                  </Link>

                  <p className="text-gray-400 text-sm line-clamp-3"
                  >
                    {post.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-400">
                      <UserIcon className="w-4 h-4 mr-1" />
                      {post.author}
                    </div>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="text-naqd-green hover:text-naqd-light transition-colors flex items-center text-sm"
                    >
                      Read More
                      <ArrowRightIcon className="w-4 h-4 ml-1" />
                    </Link>
                  </div>

                  <div className="flex flex-wrap gap-1"
                  >
                    {post.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-white/10 text-gray-300 px-2 py-1 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="section-padding bg-naqd-dark-gray/50">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <div className="animate-on-scroll">
              <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
              <p className="text-gray-300 mb-6"
              >
                Subscribe to our newsletter for the latest tax updates, compliance tips,
                and business insights delivered to your inbox.
              </p>
              <form className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:border-naqd-green focus:outline-none"
                />
                <button type="submit" className="btn-primary"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;